/**
 * Mis vehículos (`/vehiculos`).
 *
 * Portado de `PortalCliente::vehiculos()` + `api_vehiculos` (`customer_vehicles`).
 * El cliente da de alta sus placas y las mantiene al día; la **placa no se edita**
 * después (igual que en el portal anterior: es la que identifica el vehículo en
 * los descuentos y en los despachos), sólo sus datos descriptivos.
 *
 * Las bajas son lógicas (`status = 0`), así que la lista muestra también los
 * vehículos dados de baja con su etiqueta, para que no «desaparezcan».
 */

import { Car, Pencil, Plus, RotateCcw, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Buscador } from '@/components/ui/buscador';
import { Button } from '@/components/ui/button';
import { Paginador } from '@/components/ui/paginador';
import { SectionCard } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { EstadoVacio } from '@/components/ui/estado-vacio';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { TituloPagina } from '@/components/ui/titulo-pagina';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { coincideBusqueda } from '@/lib/buscar';
import { ApiError } from '@/lib/http/api-error';

import { useGuardarVehiculo, useInhabilitarVehiculo, usePortalVehiculos } from '../portal.hooks';
import type { VehiculoFormulario, VehiculoPortal } from '../portal.types';

/** Formulario vacío. */
const VACIO: VehiculoFormulario = { placa: '', tipo: '', marca: '', modelo: '', anio: '' };

/** Mensaje legible de un error de la API. */
function mensajeDe(error: unknown, porDefecto: string): string {
  return error instanceof ApiError ? error.message : porDefecto;
}

export function PortalVehiculosPage() {
  useDocumentTitle('Portal · Vehículos');
  const consulta = usePortalVehiculos();
  const guardar = useGuardarVehiculo();
  const darDeBaja = useInhabilitarVehiculo();

  const [formulario, setFormulario] = useState<VehiculoFormulario | null>(null);
  const [editando, setEditando] = useState<VehiculoPortal | null>(null);
  const [porBaja, setPorBaja] = useState<VehiculoPortal | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  const abrirAlta = () => {
    setEditando(null);
    setFormulario({ ...VACIO });
  };

  const abrirEdicion = (vehiculo: VehiculoPortal) => {
    setEditando(vehiculo);
    setFormulario({
      placa: vehiculo.placa,
      tipo: vehiculo.tipo,
      marca: vehiculo.marca,
      modelo: vehiculo.modelo,
      anio: vehiculo.anio === null ? '' : String(vehiculo.anio),
    });
  };

  const enviar = () => {
    if (!formulario) return;

    guardar.mutate(
      { id: editando?.id, datos: formulario },
      {
        onSuccess: () => {
          mostrar(editando ? 'Vehículo actualizado.' : 'Vehículo registrado.');
          setFormulario(null);
          setEditando(null);
        },
      },
    );
  };

  const confirmarBaja = () => {
    if (!porBaja) return;

    darDeBaja.mutate(porBaja.id, {
      onSuccess: () => {
        mostrar(`La placa ${porBaja.placa} quedó dada de baja.`);
        setPorBaja(null);
      },
    });
  };

  const vehiculos = useMemo(() => consulta.data?.vehiculos ?? [], [consulta.data]);
  const activos = vehiculos.filter((item) => item.activo).length;

  const [busqueda, setBusqueda] = useState('');

  const filtrados = useMemo(
    () =>
      vehiculos.filter((vehiculo) =>
        coincideBusqueda(
          [
            vehiculo.placa,
            vehiculo.tipo,
            vehiculo.marca,
            vehiculo.modelo,
            vehiculo.anio,
            vehiculo.activo ? 'activo' : 'dado de baja',
          ],
          busqueda,
        ),
      ),
    [vehiculos, busqueda],
  );

  const paginacion = usePaginacion(filtrados, { clave: busqueda });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <TituloPagina
          icono={Car}
          titulo="Mis vehículos"
          descripcion="Las placas que usas en la estación. Los despachos a crédito se registran con ellas."
        />
        <Button
          size="sm"
          leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
          onClick={abrirAlta}
        >
          Agregar vehículo
        </Button>
      </div>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {consulta.error ? (
        <Alert variant="error">
          {mensajeDe(consulta.error, 'No se pudieron cargar tus vehículos.')}
        </Alert>
      ) : null}

      <SectionCard
        title={`Vehículos (${vehiculos.length})`}
        description={
          vehiculos.length
            ? `${busqueda.trim() ? `${filtrados.length} de ${vehiculos.length} · ` : ''}${activos} activo(s) de ${vehiculos.length}`
            : 'Placas asociadas a tu cuenta.'
        }
      >
        <div className="flex flex-col gap-3">
          <Buscador
            valor={busqueda}
            onChange={setBusqueda}
            ariaLabel="Buscar vehículos"
            placeholder="Buscar placa, marca o modelo…"
          />

          {consulta.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : vehiculos.length === 0 ? (
            <EstadoVacio
              icon={Car}
              titulo="Todavía no registras vehículos"
              detalle="Registra tu primera placa para agilizar tus despachos."
              accion={
                <Button
                  size="sm"
                  leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
                  onClick={abrirAlta}
                >
                  Agregar vehículo
                </Button>
              }
            />
          ) : filtrados.length === 0 ? (
            <EstadoVacio
              icon={Search}
              titulo="Sin coincidencias"
              detalle={`No encontramos vehículos para “${busqueda.trim()}”.`}
              accion={
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
                  onClick={() => setBusqueda('')}
                >
                  Quitar búsqueda
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[11px] tracking-wide text-ink-400 uppercase">
                  <th className="py-2 font-semibold">Placa</th>
                  <th className="hidden py-2 font-semibold sm:table-cell">Tipo</th>
                  <th className="py-2 font-semibold">Marca / modelo</th>
                  <th className="hidden py-2 font-semibold sm:table-cell">Año</th>
                  <th className="py-2 font-semibold">Estado</th>
                  <th className="py-2 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginacion.visibles.map((vehiculo) => (
                  <tr
                    key={vehiculo.id}
                    className="border-b border-ink-50 transition-colors hover:bg-brand-50/40"
                  >
                    <td className="py-2">
                      <span className="font-mono text-sm font-semibold text-ink-900">
                        {vehiculo.placa}
                      </span>
                    </td>
                    <td className="hidden py-2 text-ink-600 sm:table-cell">{vehiculo.tipo || '—'}</td>
                    <td className="py-2 text-ink-600">
                      {[vehiculo.marca, vehiculo.modelo].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="hidden py-2 text-ink-600 sm:table-cell">
                      {vehiculo.anio ?? '—'}
                    </td>
                    <td className="py-2">
                      {vehiculo.activo ? (
                        <Badge variant="success">activo</Badge>
                      ) : (
                        <Badge variant="neutral">dado de baja</Badge>
                      )}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Editar datos del vehículo"
                          onClick={() => abrirEdicion(vehiculo)}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {vehiculo.activo ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Dar de baja"
                            onClick={() => setPorBaja(vehiculo)}
                          >
                            <Trash2 className="size-4 text-danger-500" aria-hidden="true" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        <Paginador
          {...paginacion}
          etiqueta="vehículos"
          className="-mx-5 -mb-5 mt-3 rounded-b-card"
        />
      </SectionCard>

      <Dialog
        open={formulario !== null}
        onClose={() => {
          setFormulario(null);
          setEditando(null);
        }}
        title={editando ? `Editar ${editando.placa}` : 'Nuevo vehículo'}
        description={
          editando
            ? 'La placa no se puede cambiar; actualiza la marca, el modelo o el año.'
            : 'Registra la placa tal como aparece en la tarjeta de identificación vehicular.'
        }
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFormulario(null);
                setEditando(null);
              }}
            >
              Cancelar
            </Button>
            <Button size="sm" loading={guardar.isPending} onClick={enviar}>
              {editando ? 'Guardar cambios' : 'Registrar vehículo'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Input
            label="Placa"
            value={formulario?.placa ?? ''}
            disabled={editando !== null}
            hint={editando ? 'La placa no se puede cambiar.' : 'Ej. B7M747'}
            onChange={(evento) =>
              setFormulario((previo) => ({ ...(previo ?? VACIO), placa: evento.target.value.toUpperCase() }))
            }
          />
          <Input
            label="Tipo de vehículo"
            value={formulario?.tipo ?? ''}
            placeholder="CAMIONETA, CAMIÓN, AUTOMÓVIL…"
            onChange={(evento) =>
              setFormulario((previo) => ({ ...(previo ?? VACIO), tipo: evento.target.value }))
            }
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Marca"
              value={formulario?.marca ?? ''}
              onChange={(evento) =>
                setFormulario((previo) => ({ ...(previo ?? VACIO), marca: evento.target.value }))
              }
            />
            <Input
              label="Modelo"
              value={formulario?.modelo ?? ''}
              onChange={(evento) =>
                setFormulario((previo) => ({ ...(previo ?? VACIO), modelo: evento.target.value }))
              }
            />
          </div>
          <Input
            label="Año"
            inputMode="numeric"
            value={formulario?.anio ?? ''}
            onChange={(evento) =>
              setFormulario((previo) => ({ ...(previo ?? VACIO), anio: evento.target.value }))
            }
          />

          {guardar.error ? (
            <Alert variant="error">
              {mensajeDe(
                guardar.error,
                editando ? 'No se pudo actualizar el vehículo.' : 'No se pudo registrar el vehículo.',
              )}
            </Alert>
          ) : null}
        </div>
      </Dialog>

      <ConfirmDialog
        open={porBaja !== null}
        title="Dar de baja el vehículo"
        description={
          porBaja
            ? `La placa ${porBaja.placa} dejará de aparecer como activa, pero su historial de despachos se conserva.`
            : ''
        }
        confirmLabel="Dar de baja"
        loading={darDeBaja.isPending}
        onConfirm={confirmarBaja}
        onClose={() => setPorBaja(null)}
      />
    </div>
  );
}
